import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  LayoutDashboard,
  Package,
  ShoppingCart,
  LineChart,
  Settings as SettingsIcon,
  Leaf,
  LogOut,
  Bell,
  Building2,
  Users,
} from "lucide-react";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import NotificationsPopover from "@/components/NotificationsPopover";
import StoreSwitcher from "@/components/StoreSwitcher";
import OnboardingTour from "@/components/OnboardingTour";
import { useAuth } from "@/context/AuthContext";

const baseNavItems = [
  { to: "/app/dashboard", label: "Panoramica", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/app/prodotti", label: "Prodotti", icon: Package, testid: "nav-products" },
  { to: "/app/vendite", label: "Vendite", icon: ShoppingCart, testid: "nav-sales" },
  { to: "/app/fornitori", label: "Fornitori", icon: Building2, testid: "nav-suppliers" },
  { to: "/app/analisi", label: "Analisi", icon: LineChart, testid: "nav-analytics" },
  { to: "/app/impostazioni", label: "Impostazioni", icon: SettingsIcon, testid: "nav-settings" },
];

const teamNavItem = { to: "/app/team", label: "Team", icon: Users, testid: "nav-team" };

export default function AppLayout() {
  const navigate = useNavigate();
  const { user, team, logout: authLogout } = useAuth();
  const [alertCount, setAlertCount] = useState(0);

  // Show Team nav only to users with team_manage permission (i.e. owners)
  const isOwner = team?.role === "owner" && !team?.is_guest;
  const navItems = isOwner ? [...baseNavItems, teamNavItem] : baseNavItems;

  useEffect(() => {
    const fetchAlerts = async () => {
      try {
        const data = await api.getAlerts();
        setAlertCount(data.length);
      } catch (_e) {
        // ignore
      }
    };
    fetchAlerts();
    const t = setInterval(fetchAlerts, 30000);
    return () => clearInterval(t);
  }, []);

  const logout = async () => {
    await authLogout();
    navigate("/");
  };

  const isGuest = !user;
  const displayName = user?.name || "Utente Ospite";
  const displaySub = user?.email || "Modalità demo";
  const initials = (user?.name || "OS").split(" ").map((s) => s[0]).join("").slice(0, 2).toUpperCase();

  return (
    <div className="min-h-screen bg-[#F9F8F6] flex">
      <OnboardingTour />
      {/* Sidebar */}
      <aside className="hidden lg:flex w-64 flex-col bg-[#F4F1ED] border-r border-[#EAE7E1] sticky top-0 h-screen">
        <div className="p-6 flex items-center gap-2 border-b border-[#EAE7E1]">
          <div className="h-9 w-9 rounded-xl bg-[#2E4A3B] flex items-center justify-center">
            <Leaf size={18} className="text-white" />
          </div>
          <div>
            <div className="text-sm font-semibold tracking-tight" style={{ fontFamily: "Work Sans, sans-serif" }}>
              In<span className="text-[#2E4A3B]">Palmo</span>
            </div>
            <div className="text-[11px] text-[#8E918C] uppercase tracking-wider">Il tuo magazzino</div>
          </div>
        </div>

        <nav className="flex-1 p-3 space-y-1">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={item.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium ${
                  isActive ? "sidebar-link-active" : "sidebar-link"
                }`
              }
            >
              <item.icon size={18} />
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="p-3 border-t border-[#EAE7E1]">
          <div className="flex items-center gap-3 p-2 rounded-lg">
            <Avatar className="h-9 w-9">
              {user?.picture ? <AvatarImage src={user.picture} alt={displayName} /> : null}
              <AvatarFallback className="bg-[#2E4A3B] text-white text-xs">{initials}</AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <div data-testid="sidebar-user-name" className="text-sm font-medium truncate">{displayName}</div>
              <div data-testid="sidebar-user-sub" className="text-xs text-[#8E918C] truncate">{isGuest ? "Modalità demo" : displaySub}</div>
            </div>
            <Button
              data-testid="logout-btn"
              variant="ghost"
              size="icon"
              onClick={logout}
              className="text-[#5C5F5A] hover:text-[#C65D47] hover:bg-[#FCEAE8]"
              title="Esci"
            >
              <LogOut size={16} />
            </Button>
          </div>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top bar */}
        <header className="sticky top-0 z-20 bg-[#F9F8F6]/80 backdrop-blur-md border-b border-[#EAE7E1]">
          <div className="flex items-center justify-between px-6 lg:px-10 py-4">
            <div className="lg:hidden flex items-center gap-2">
              <div className="h-8 w-8 rounded-lg bg-[#2E4A3B] flex items-center justify-center">
                <Leaf size={16} className="text-white" />
              </div>
              <span className="font-semibold" style={{ fontFamily: "Work Sans, sans-serif" }}>
                In<span className="text-[#2E4A3B]">Palmo</span>
              </span>
            </div>
            <div className="hidden lg:block"></div>
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="hidden sm:block">
                <StoreSwitcher />
              </div>
              <NotificationsPopover alertCount={alertCount} onCount={setAlertCount} />
              {/* Mobile/tablet user menu — sidebar logout is hidden below lg */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    data-testid="user-menu-btn"
                    className="lg:hidden flex items-center rounded-full focus:outline-none focus:ring-2 focus:ring-[#2E4A3B]/30"
                    aria-label="Menu utente"
                  >
                    <Avatar className="h-8 w-8">
                      {user?.picture ? <AvatarImage src={user.picture} alt={displayName} /> : null}
                      <AvatarFallback className="bg-[#2E4A3B] text-white text-[11px]">{initials}</AvatarFallback>
                    </Avatar>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56" data-testid="user-menu-content">
                  <DropdownMenuLabel className="flex flex-col gap-0.5">
                    <span className="text-sm font-semibold truncate">{displayName}</span>
                    <span className="text-xs font-normal text-[#8E918C] truncate">{isGuest ? "Modalità demo" : displaySub}</span>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    data-testid="logout-mobile-btn"
                    onClick={logout}
                    className="text-[#C65D47] focus:text-[#C65D47] focus:bg-[#FCEAE8] cursor-pointer"
                  >
                    <LogOut size={14} className="mr-2" />
                    Esci
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
          {/* Mobile nav */}
          <div className="lg:hidden flex overflow-x-auto border-t border-[#EAE7E1] px-4 py-2 gap-1 bg-[#F4F1ED]">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={`${item.testid}-mobile`}
                className={({ isActive }) =>
                  `flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium whitespace-nowrap ${
                    isActive ? "sidebar-link-active" : "sidebar-link"
                  }`
                }
              >
                <item.icon size={14} />
                <span>{item.label}</span>
              </NavLink>
            ))}
          </div>
        </header>

        <motion.main
          key={window.location.pathname}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="flex-1 px-6 lg:px-10 py-8 lg:py-10"
        >
          <Outlet />
        </motion.main>
      </div>
    </div>
  );
}
